import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import Button from '@components/Button';
import IconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import { ExpertsBlockConfig } from '@components/pages/PageTemplates/interfaces';
import Profile from '@components/pages/SubscribeTest/OfferSelection/Experts/Profile';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { Reporter } from '@utils/backend-api/subscribe';
import { WIDTH_SM } from '@utils/constants';

import ExpertsBlockFeaturedVideo from './FeaturedVideo';

interface CarouselProps {
  className?: string;
  config?: ExpertsBlockConfig;
  reporters: Reporter[];
}

const JING_YANG_NAME = 'Jing Yang';

enum SlideType {
  Profile = 'profile',
  Video = 'video',
}

const Carousel = ({ className, config, reporters }: CarouselProps) => {
  const { trackEvent } = useEventTracking({ location: TrackingLocation.Experts });
  const [currentIndex, setCurrentIndex] = useState(0);
  const { width } = useWindowSize();
  const isMobile = width < WIDTH_SM;
  const videoRef = useRef<HTMLVideoElement>(null);

  const slides = reporters.flatMap((reporter) => {
    if (reporter.name === JING_YANG_NAME) {
      return isMobile
        ? [{ type: SlideType.Profile, reporter }]
        : [
            { type: SlideType.Video, reporter },
            { type: SlideType.Profile, reporter },
          ];
    }
    return [{ type: SlideType.Profile, reporter }];
  });

  const totalSlides = slides.length;

  const nextSlide = () => {
    trackEvent(click({ clickTarget: ClickTarget.NextSlide }));
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  };

  const prevSlide = () => {
    trackEvent(click({ clickTarget: ClickTarget.PreviousSlide }));
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
  };

  useEffect(() => {
    if (currentIndex != 0 && videoRef.current) {
      videoRef.current.pause();
    }
  }, [currentIndex]);

  return (
    <div
      className={clsx(
        'relative mx-auto mt-48 w-full max-w-[820px] max-sm:overflow-hidden',
        className
      )}
    >
      <div
        className="flex items-center transition-transform duration-500"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => {
          const isPrev = index === (currentIndex - 1 + totalSlides) % totalSlides;
          const isNext = index === (currentIndex + 1) % totalSlides;

          const gradientClasses = clsx(
            "relative w-full flex-shrink-0 before:absolute before:top-0 before:h-full before:w-full before:content-['']",
            {
              '[mask-image:linear-gradient(to_left,black,transparent)] before:right-0 before:bg-gradient-white-l':
                isPrev,
              '[mask-image:linear-gradient(to_right,black,transparent)] before:left-0 before:bg-gradient-white-r':
                isNext,
              'flex items-center justify-center': slide.type === SlideType.Video,
            }
          );

          if (slide.type === SlideType.Video) {
            return (
              <div key={`video-${index}`} className={gradientClasses}>
                <div className="flex max-w-[358px] flex-col items-center justify-center sm:max-w-[500px] md:max-w-[600px]">
                  <ExpertsBlockFeaturedVideo
                    videoRef={videoRef}
                    poster={config?.poster_image}
                    src={config?.video_url}
                    title={config?.video_title}
                  />
                </div>
              </div>
            );
          }

          return (
            <div key={`profile-${index}`} className={gradientClasses}>
              <Profile reporter={slide.reporter} />
            </div>
          );
        })}
      </div>

      <Button
        as="button"
        title="Show previous slide"
        className="absolute left-0 top-[138px] touch-manipulation border-0 bg-transparent p-0 sm:top-1/2 lg:-left-[60px]"
        onClick={prevSlide}
      >
        <IconArrowDown className="h-20 rotate-90 fill-grayscale-gray hover:fill-primary-magenta" />
      </Button>
      <Button
        as="button"
        title="Show next slide"
        className="absolute right-0 top-[138px] touch-manipulation border-0 bg-transparent p-0 sm:top-1/2 lg:-right-[60px]"
        onClick={nextSlide}
      >
        <IconArrowDown className="h-20 -rotate-90 fill-grayscale-gray hover:fill-primary-magenta" />
      </Button>
    </div>
  );
};

export default Carousel;
